'use client'

import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { I18nContext, I18nContextValue } from '@/components/providers/I18nProvider'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{error?: Error; resetError: () => void}>
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = I18nContext
  declare context: I18nContextValue

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
    this.setState({
      error,
      errorInfo
    })
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  render() {
    const t = this.context?.t ?? ((key: string) => key)

    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          {/* NOTE: keep this fallback free of components that call useI18n() —
              ErrorBoundary sits ABOVE I18nProvider in the tree, so any useI18n
              here would rethrow "must be used within an I18nProvider" and mask
              the original error. Use plain elements + this.context?.t. */}
          <div className="w-full max-w-md rounded-xl border bg-card py-6 shadow-sm">
            <div className="px-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="leading-none font-semibold text-red-900 dark:text-red-100">{t('errorBoundary.title')}</div>
              <p className="text-muted-foreground text-sm mt-2">{t('errorBoundary.description')}</p>
            </div>
            <div className="px-6 mt-4 space-y-4">
              {this.state.error && (
                <details className="text-xs bg-muted p-3 rounded border" open>
                  <summary className="cursor-pointer font-medium">{t('errorBoundary.details')}</summary>
                  <div className="mt-2 space-y-2">
                    <div className="font-semibold text-destructive break-all">
                      {String(this.state.error.message || this.state.error) || 'Unknown error'}
                    </div>
                    {this.state.errorInfo?.componentStack && (
                      <pre className="whitespace-pre-wrap break-all text-destructive/90">
{this.state.errorInfo.componentStack}
                      </pre>
                    )}
                    {this.state.error.stack && (
                      <pre className="whitespace-pre-wrap break-all">
{this.state.error.stack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
              <button
                onClick={this.resetError}
                className="w-full rounded-md border border-border py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                <RefreshCw className="w-4 h-4 mr-2 inline" />
                {t('errorBoundary.tryAgain')}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full rounded-md bg-primary py-2 text-sm font-medium text-primary-foreground"
              >
                {t('errorBoundary.refresh')}
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook version for functional components
export function useErrorBoundary() {
  return (error: Error) => {
    throw error
  }
}
